import React from "react";

import { useStatsigClient, useStatsigUser } from "@statsig/react-bindings";
import { setCookie } from "cookies-next";
import isEqual from "lodash/isEqual";
import { UseFormReturn } from "react-hook-form";
import { useIntl } from "react-intl";

import { Breakpoints } from "@/foundations/breakpoints/breakpoints";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useWatchDebounced } from "@/lib/hooks/use-watch-debounced";
import { BookingScheduleDay } from "@/lib/types/schedule-types";
import { createValidBookingSchedule } from "@/lib/utils/schedule-utils";
import { ApiError } from "~/api/api-error";
import { ListingData, useListingData } from "~/context/ListingPageDataContext";
import { useProfileData } from "~/queries/profile/useProfile";
import { useStatsigUserContext } from "~/statsig/StatsigUserProvider";
import { BookListingEstimate, BookListingQuote } from "~/types/api-types";
import { PAY_AT_VENUE_WITH_DEPOSIT, PAY_IN_FULL } from "~/utils/constants";
import { createCookieOptions } from "~/utils/cookie-utils";

import { handleBookingPriceCreatedAnalytics } from "./book-listing-analytics";
import { fetchBookingEstimate, fetchBookingQuote } from "./book-listing-utils";
import {
  BookListingFormValues,
  useBookListingForm,
} from "./BookListingForm/use-book-listing-form";
import {
  GUEST_FEE_DISCOUNT_EXPERIMENT,
  GUEST_FEE_DISCOUNT_PARAM,
} from "./experiment.consts";
import { normalizeGuestFeeDiscountBucket } from "./guest-fee-discount.utils";
import { useSecondaryAlertConfig } from "./hooks/use-secondary-alert-config";
import { getEnforceMinimumHours } from "./listing-availability.utils";

type BookListingContext = {
  quote: BookListingQuote | undefined;
  isFetchingQuote: boolean;
  estimate: BookListingEstimate | undefined;
  isFetchingEstimate: boolean;
  bookListingForm: UseFormReturn<BookListingFormValues>;
  updateEstimate: (triggerEstimateFetch?: boolean) => Promise<void>;
  alertConfig: {
    message: string;
    isVisible: boolean;
  };
  setAlertConfig: React.Dispatch<
    React.SetStateAction<BookListingContext["alertConfig"]>
  >;
  enforceMinimumHours: boolean;
} & ReturnType<typeof useSecondaryAlertConfig>;

export const BookListingContext = React.createContext({} as BookListingContext);

type BookListingProviderProps = React.PropsWithChildren;

export const BookListingContextProvider = ({
  children,
}: BookListingProviderProps) => {
  const { secondaryDataFetched, data: listingData = {} as ListingData } =
    useListingData();
  const profile = useProfileData();

  const intl = useIntl();

  const [alertConfig, setAlertConfig] = React.useState<
    BookListingContext["alertConfig"]
  >({
    message: "",
    isVisible: false,
  });

  const secondaryAlertConfig = useSecondaryAlertConfig();

  // estimate is used before we have enough details to request a quote (FSVs only, for now)
  const [estimate, setEstimate] = React.useState<
    BookListingEstimate | undefined
  >(undefined);
  const [isFetchingEstimate, setIsFetchingEstimate] = React.useState(false);

  // quote is used after dates are selected and quote is fetched
  const [quote, setQuote] = React.useState<BookListingQuote | undefined>(
    undefined,
  );
  const [isFetchingQuote, setIsFetchingQuote] = React.useState(false);

  const isLGScreen = useMediaQuery(`(min-width: ${Breakpoints.LG}px)`);

  const { user } = useStatsigUser();

  // The quote's guest-fee bucket depends on these attrs being on the Statsig user
  // (pushed by useStatsigListingPageUserUpdate). Wait for them before the first
  // fetch so the quote carries the resolved bucket.
  const isStatsigListingReady =
    !!listingData.listingId &&
    user.custom?.listingCountry === listingData.listingCountry &&
    user.custom?.full_service === !!listingData.fullService?.active;

  const { setAttributes } = useStatsigUserContext();
  const { getExperiment } = useStatsigClient();

  const enforceMinimumHours = getEnforceMinimumHours(listingData);

  const initialSchedule = React.useMemo(() => {
    return (
      createValidBookingSchedule(listingData.defaultBookingSchedule, {
        onAutoCorrect: (modificationType) => {
          if (modificationType === "minDuration" && !alertConfig.message) {
            setAlertConfig({
              isVisible: true,
              message: intl.formatMessage(
                {
                  defaultMessage:
                    "Your estimate was updated to meet venue's {minDuration} hr minimum.",
                },
                {
                  minDuration: listingData.listingMinimumDuration,
                },
              ),
            });
          }
        },
        fixInvalidDuration: true,
        exactDuration: listingData.fullService?.active
          ? listingData.fullService?.min_booking_duration_hours
          : 0,
        minDuration:
          !listingData.fullService?.active && enforceMinimumHours
            ? parseFloat(listingData.listingMinimumDuration ?? "0")
            : 0,
      }) || []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bookListingForm = useBookListingForm({
    defaultValues: {
      schedule: initialSchedule,
      attendeePricingTierMax:
        listingData.listingAttendeePricing?.tiers?.[0]?.max || 1,
    },
  });

  const saveValuesToCookies = (
    schedule: BookingScheduleDay[],
    attendeePricingTierMax: number,
  ) => {
    const twoWeeksInMilliseconds = 14 * 24 * 60 * 60 * 1000;
    const cookieOptions = createCookieOptions({
      expires: schedule?.[0]
        ? new Date(Date.now() + twoWeeksInMilliseconds)
        : undefined,
    });

    setCookie("booking_schedule", JSON.stringify(schedule), cookieOptions);

    const selectedTier = (listingData.listingAttendeePricing?.tiers || []).find(
      (tier) => tier.max === +attendeePricingTierMax,
    );

    if (selectedTier) {
      const cookieOptions = createCookieOptions();
      setCookie(
        "booking_attendee_tier",
        JSON.stringify({ [listingData.listingId]: selectedTier }),
        cookieOptions,
      );
    }
  };

  const handlePriceFetchError = (error: unknown) => {
    setIsFetchingEstimate(false);
    setIsFetchingQuote(false);

    if (error instanceof ApiError) {
      const errorMessage = error.errorData?.body?.body?.message;
      window.analyticsIntegrations.availabilityBlock(
        { schedule: bookListingForm.getValues().schedule },
        listingData.useType,
        listingData.metro,
        errorMessage ?? "",
      );

      bookListingForm.setError("root", {
        type: "manual",
        message:
          errorMessage ||
          intl.formatMessage({
            defaultMessage:
              "Something went wrong. Please refresh the page and try again.",
          }),
      });
    }
  };

  const updateEstimate = async () => {
    if (!listingData?.fullService?.active) {
      return;
    }

    try {
      setIsFetchingEstimate(true);
      const estimate = await fetchBookingEstimate({
        listingId: listingData.listingId,
        listingCountry: listingData.listingCountry,
      });
      setEstimate(estimate);
      setIsFetchingEstimate(false);
    } catch (error) {
      handlePriceFetchError(error);
    }
  };

  const updateQuote = async ({
    schedule,
    attendeePricingTierMax,
  }: BookListingFormValues) => {
    bookListingForm.clearErrors("root");

    const validSchedule = createValidBookingSchedule(schedule); // no auto-correction here, its handled in the ScheduleInputField component

    if (!validSchedule?.length) {
      if (!estimate) {
        updateEstimate();
      }
      return;
    }
    setIsFetchingQuote(true);
    saveValuesToCookies(validSchedule, attendeePricingTierMax);
    const paymentOption = listingData.payAtVenue?.active
      ? PAY_AT_VENUE_WITH_DEPOSIT
      : PAY_IN_FULL;
    try {
      // Sync this listing's targeting attrs onto the Statsig user and wait for
      // the update to settle, then read the resolved guest-fee bucket so the
      // quote request carries the correctly-targeted value.
      await setAttributes({
        full_service: !!listingData.fullService?.active,
        listingCountry: listingData.listingCountry,
      });
      const guestFeeTestGroup = normalizeGuestFeeDiscountBucket(
        getExperiment(GUEST_FEE_DISCOUNT_EXPERIMENT).get(
          GUEST_FEE_DISCOUNT_PARAM,
          "full",
        ),
      );

      const quote = await fetchBookingQuote({
        schedule: validSchedule,
        attendeePricingTierMax,
        hostId: listingData.listingOwnerId,
        guestId: profile?.SSOid || "anonymous",
        listingId: listingData.listingId,
        spaceId: listingData.listingParentSpaceId,
        listingPrice: listingData.listingPrice,
        gaClientId: profile?.gaClientId || "",
        locale: intl.locale,
        enforceMinimumHours,
        paymentOption,
        guestFeeTestGroup,
      });

      if (isLGScreen) {
        handleBookingPriceCreatedAnalytics(quote!, validSchedule, listingData);
      }

      setQuote(quote);
      setIsFetchingQuote(false);
    } catch (error) {
      handlePriceFetchError(error);
      // if initial quote fails (e.g. cookie schedule has conflicts), try to get an estimate
      if (!estimate && !quote) {
        updateEstimate();
      }
    }
  };

  const watchFields = useWatchDebounced({
    control: bookListingForm.control,
    name: ["schedule", "attendeePricingTierMax"],
  });

  const prevFieldValues = React.useRef<BookListingFormValues | undefined>(
    undefined,
  );
  React.useEffect(() => {
    // need to wait to see if there is a profile before updating the quote
    if (!secondaryDataFetched) {
      return;
    }

    // Don't record field values (or fetch) until the listing attrs are on the
    // Statsig user — otherwise the fetch after the sync would be skipped, and the
    // quote would carry the default bucket.
    if (!isStatsigListingReady) {
      return;
    }

    // `updateQuote` awaits the Statsig user sync internally, so the quote
    // payload always carries the resolved bucket — no experiment-loading gate
    // needed here.
    const [schedule, attendeePricingTierMax] = watchFields;
    const nextFieldValues = { schedule, attendeePricingTierMax };
    // Compare against the same object shape we store in the ref — comparing
    // the ref object to the watchFields array always fails isEqual.
    const fieldsHaveChanged = !isEqual(
      prevFieldValues.current,
      nextFieldValues,
    );

    if (fieldsHaveChanged) {
      prevFieldValues.current = nextFieldValues;
      updateQuote({ schedule, attendeePricingTierMax });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchFields, secondaryDataFetched, isStatsigListingReady]);

  return (
    <BookListingContext.Provider
      value={{
        quote,
        isFetchingQuote,
        estimate,
        isFetchingEstimate,
        bookListingForm,
        updateEstimate,
        alertConfig,
        setAlertConfig,
        enforceMinimumHours,
        ...secondaryAlertConfig,
      }}
    >
      {children}
    </BookListingContext.Provider>
  );
};

export const useBookListingContext = () => {
  const context = React.useContext(BookListingContext);
  if (!context) {
    throw new Error(
      "useBookListingContext must be used within a BookListingContext",
    );
  }
  return context;
};
